import React, { useCallback, useEffect, useState } from "react";
import EditElementComponent from "./EditElementComponents";
import BuilderTabs from "../../Layouts/BuilderTabs";
import { AdvanceEditForDetailviewField } from "../../Pages/ApplicationBlock/DetailsViewRenderer/Desktop/EditDetailsviewFields";
import Input from "../FormComponents/Input";
import { debounce } from "../../commonFunctions/arrayFunc";
import ElementPositionControls from "./ElementPositionControls";
import ClassSelectorLayout from "../ClassSelectorLayout/ClassSelectorLayout";

const EditPositionFields = ({
  editElementRightsidebar,
  saveElement,
  smId,
  tableType = "statemachine",
  device,
  handleClasses,
  handleStyle,
  jobPlaces = "details_view",
  targetFolderPath,
  gridSize = 4
}) => {
  const [editElementData, setEditElementData] = useState(null);
  const [appliedClasses, setAppliedClasses] = useState(null);
  const [elementType, setElementType] = useState("");
  const [elementSubType, setElementSubType] = useState("");
  const [elementId, setElementId] = useState("");
  useEffect(() => {
    setEditElementData(editElementRightsidebar?.config);
    setElementType(editElementRightsidebar?.type);
    setElementId(editElementRightsidebar?.id);
    setElementSubType(editElementRightsidebar?.subType);
    if (editElementRightsidebar?.type === "table" && editElementRightsidebar?.subType) {
      setAppliedClasses(editElementRightsidebar?.config.classes || []);
    } else {
      setAppliedClasses(editElementRightsidebar?.baseElement?.classes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editElementRightsidebar.id,
    editElementRightsidebar?.type,
    editElementRightsidebar?.subType, // table header and row edit
    editElementRightsidebar?.config.id,
    editElementRightsidebar?.config.cell_id // on diffrent cell click
  ]);

  useEffect(() => {
    if (
      !editElementData ||
      editElementData.type === "html" ||
      editElementData.type === "dynamic-html" ||
      editElementData.type === "form"
      // editElementData.type === "button" ||
      // editElementData.type === "dropdown"
    )
      return;

    const debounceTimeout = setTimeout(() => {
      saveElement(editElementData, elementId);
    }, 200);

    return () => {
      clearTimeout(debounceTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editElementData]);

  useEffect(() => {
    handleClasses(appliedClasses, elementId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedClasses]);

  const handleEditElementData = (type, data) => {
    setEditElementData({ ...editElementData, [type]: data });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleStyle = useCallback(
    debounce((style) => {
      handleStyle(style, elementId);
    }, 300),
    [elementId] // dependencies
  );

  const handleChangeStyle = (style) => {
    debouncedHandleStyle(style);
  };

  if (!elementId) return;
  const tabData = [
    {
      label: "Basic",
      content: (
        <div className="form_basic_field_wrapper sidebar-edit-option-outer">
          <ElementPositionControls
            dimensions={editElementRightsidebar.baseElement.style}
            type={editElementRightsidebar.baseElement.type}
            onChange={handleChangeStyle}
            gridSize={gridSize}
          />
          <EditElementComponent
            elements={editElementRightsidebar.elements}
            editElementData={editElementData}
            setEditElementData={setEditElementData}
            saveElement={(config) => saveElement(config, elementId)}
            smId={smId}
            device={device}
            tableType={tableType}
            elementType={elementType}
            elementSubType={elementSubType}
            jobPlaces={jobPlaces}
            targetFolderPath={targetFolderPath}
            handleClasses={handleClasses}
          />
          <DisableResizeOption editElementData={editElementData} setEditElementData={setEditElementData} />
        </div>
      )
    },

    {
      label: "Style",
      content: (
        <ClassSelectorLayout
          id={elementId}
          type={editElementRightsidebar.type}
          defaultSelected={appliedClasses}
          onChange={setAppliedClasses}
        />
      )
    },
    {
      label: "Advanced",
      content: (
        <div style={{ overflow: "visible" }} className="form_basic_field_wrapper sidebar-edit-option-outer">
          <AdvanceEditForDetailviewField
            editElementData={editElementData}
            onChange={handleEditElementData}
            tableID={smId}
            device={device}
          />
        </div>
      )
    }
  ];

  const returnTypeBasedEditComponents = () => {
    const excludedTypes = ["html", "dynamic-html", "spacer", "form"];

    const isExcludedElement = excludedTypes.includes(elementType);
    const isTableCell =
      elementType === "table" && (elementSubType === "edit_row_cell" || elementSubType === "edit_column_cell");
    const rvTableColumn = elementType === "rv_table" && elementSubType === "edit_column";
    const isExcluded = isExcludedElement || isTableCell || rvTableColumn;
    if (isExcluded) {
      return (
        <div className="form_basic_field_wrapper sidebar-edit-option-outer" style={{ padding: "0px" }}>
          {["button-group", "html", "dynamic-html", "file_viewer", "spacer", "form"].includes(elementType) && (
            <div className="mt_3 ml_3">
              <ElementPositionControls
                dimensions={editElementRightsidebar.baseElement.style}
                type={editElementRightsidebar.baseElement.type}
                onChange={handleChangeStyle}
                gridSize={gridSize}
              />
            </div>
          )}
          <EditElementComponent
            elements={editElementRightsidebar.elements}
            editElementData={editElementData}
            saveElement={(config) => saveElement(config, elementId)}
            setEditElementData={setEditElementData}
            smId={smId}
            device={device}
            tableType={tableType}
            handleClasses={handleClasses}
            elementType={elementType}
            elementSubType={elementSubType}
            jobPlaces={jobPlaces}
            targetFolderPath={targetFolderPath}
          />
          {isExcludedElement && (
            <div className="ml_3">
              <DisableResizeOption editElementData={editElementData} setEditElementData={setEditElementData} />
            </div>
          )}
        </div>
      );
    } else {
      return <BuilderTabs tabs={tabData} contentClass="tab-content-scroll" />;
    }
  };

  return <div>{editElementData && returnTypeBasedEditComponents()}</div>;
};

const DisableResizeOption = ({ editElementData, setEditElementData }) => {
  const handleChange = (e) => {
    setEditElementData({ ...editElementData, disable_resize_in_fullscreen: e.target.checked });
  };
  return (
    <Input
      type="checkbox"
      checked={editElementData.disable_resize_in_fullscreen || false}
      label="Disable Resize"
      onChange={handleChange}
    />
  );
};

export default EditPositionFields;
